<template>
  <div class="overflow-visible">
    <div class="flex flex-wrap items-center justify-between pb-4 py-1">
      <div class="flex items-center space-x-4">
        <div class="relative">
          <!-- Button to toggle dropdown -->
          <button
            id="dropdownRadioButton"
            class="inline-flex items-center text-gray-400 bg-white border border-gray-300 rounded-md px-6 py-2"
            type="button"
            @click="toggleDropdown"
          >
            <svg
              class="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z"
              />
            </svg>
            {{ selectedFilter ? formatLabel(selectedFilter) : "Filters" }}
            <svg
              class="w-2.5 h-2.5 ms-2.5"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m1 1 4 4 4-4"
              />
            </svg>
          </button>

          <!-- Dropdown menu -->
          <div
            v-if="isDropdownVisible"
            class="absolute z-10 mt-2 bg-white border border-gray-300 rounded-md shadow-lg w-40"
          >
            <ul class="py-2 text-sm text-gray-700">
              <li>
                <button
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="applyFilter('phoneNumber')"
                >
                  Phone Number
                </button>
              </li>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="applyFilter('idNumber')"
                >
                  ID Number
                </button>
              </li>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="applyFilter('FullName')"
                >
                  Full Name
                </button>
              </li>
            </ul>
          </div>
          <!-- start dropdown filter -->
          <template v-if="currentTab === 'patient-queue'">
            <div
              v-show="openDropdown"
              id="filterDropdown"
              class="absolute z-10 mt-2 w-80 p-4 bg-white border border-gray-300 rounded-lg shadow-lg"
            >
              <div class="flex">
                <button
                  type="button"
                  class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                  @click="closeDropdown"
                  aria-label="Close"
                >
                  <span class="sr-only">Dismiss</span>
                  <svg
                    class="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                </button>
              </div>
              <form class="max-w-sm mx-auto" @submit.prevent="applyFilters">
                <div class="flex justify-end space-x-2">
                  <button
                    type="button"
                    @click="cancelFilters"
                    class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-lg hover:bg-custom-yellow-dark"
                  >
                    Filter
                  </button>
                </div>
              </form>
            </div>
          </template>
        </div>

        <div class="relative flex">
          <input
            type="text"
            v-model="searchQuery"
            id="table-search"
            class="px-4 py-2 text-gray-700 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
            placeholder="Search"
          />
          <button
            @click="triggerSearch"
            class="bg-custom-yellow text-white rounded-r px-2 md:px-3 py-0 md:py-1"
          >
            <svg
              class="w-6 h-6 text-gray-100 dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
              />
            </svg>
          </button>
        </div>
      </div>

      <div class="flex items-center space-x-4 mt-4 sm:mt-0">
        <template v-if="currentTab === 'patient-queue'">
          <CustomButton
            v-if="activeUserType === 'Receptionist'"
            label="Register Patient"
            :icon-path="'M5 12h14m-7 7V5'"
            @click="handleNewPatientRegistrationModal"
            bg-class="bg-custom-yellow"
          />
        </template>
      </div>
    </div>

    <div class="tabs flex border-b mb-4">
      <button
        class="px-4 py-2 -mb-px font-semibold border-b-2"
        :class="{
          'border-custom-yellow text-custom-blue':
            currentTab === 'patient-queue',
          'text-gray-500': currentTab !== 'patient-queue',
        }"
        @click="currentTab = 'patient-queue'"
      >
        Patients
      </button>
    </div>

    <div
      v-if="showNewAppointmentModal"
      class="fixed inset-0 bg-gray-800 bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center"
    >
      <div class="bg-white px-8 py-4 rounded-lg shadow-xl w-5/6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl text-gray-800 font-bold">Register Patient</h2>
          <button
            type="button"
            class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
            @click="closeAppointmentModal"
            aria-label="Close"
          >
            <span class="sr-only">Dismiss</span>
            <svg
              class="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
          </button>
        </div>
        <hr class="h-px my-4 bg-orange-200 border-0 dark:bg-orange-200" />
        <div>Form</div>
        <div class="flex justify-end space-x-2">
          <button
            type="button"
            @click="closeAppointmentModal"
            class="px-4 py-2 mb-4 bg-gray-200 text-gray-800 rounded"
          >
            Cancel
          </button>
          <button
            @click="handleSubmit()"
            class="px-4 py-2 mb-4 bg-custom-yellow text-white rounded"
          >
            <span v-if="!showLoader"> Submit </span>
            <span v-else>
              <img :src="loaderUrl" />
            </span>
          </button>
        </div>
      </div>
    </div>

    <component
      :is="currentTabComponent"
      ref="currentTabComponent"
      :search-query="searchQuery"
    ></component>
    <PatientRegModal
      :show="showPatientRegistrationModal"
      :formData="patienRegForm"
      @update:formData="patienRegForm = $event"
      :showLoader="showLoader"
      @close="closePatientRegistrationModal"
      @requests="handleRequests"
      @submit="handleSubmit"
    />
  </div>
</template>

<script>
import { initFlowbite } from "flowbite";
import CustomButton from "@/components/CustomButton.vue";
import ReceptionPatientQueueTab from "../tabs/ReceptionPatientQueueTab.vue";
import ReceptionAppointmentQueueTab from "../tabs/ReceptionAppointmentQueueTab.vue";
import PatientsListTab from "../tabs/PatientsListTab.vue";
import PatientRegModal from "../modals/PatientRegModal.vue";
import loaderUrl from "@/assets/images/loader.gif";
import PatientServices from "@/components/forms/patient/registration/PatientServices.vue";

export default {
  name: "ReceptionistDashboardPage",
  components: {
    PatientServices,
    CustomButton,
    ReceptionPatientQueueTab,
    ReceptionAppointmentQueueTab,
    PatientRegModal,
    PatientsListTab,
  },
  data() {
    return {
      currentTab: "patient-queue",
      showPatientRegistrationModal: false,
      showNewAppointmentModal: false,
      refetchPatients: false,
      isDropdownVisible: false,
      selectedFilter: null,
      loaderUrl: loaderUrl,
      tab: "tab1",
      patienRegForm: {
        patient: {
          patientId: null,
          isExisting: false,
          dateEntryMade: "",
          ageType: 1,
          title: "",
          fullName: "",
          dob: "",
          age: 0,
          gender: "",
          relationship: "",
          idNumber: "",
          visitType: "",
          bloodGroup: "",
          phoneNumber: "",
          emailAddress: "",
          occupation: "",
          maritalStatus: "",
          residence: "",
          county: "",
          nationality: "",
          registrationMethod: "Reception",
          emergencyContact: {
            fullName: "",
            relationship: "",
            phoneNumber: "",
          },
          uploadedDocuments: [],
          selectedMethod: "",
          selectedMobileProvider: "",
          agreements: {
            consentChecked: true,
            consentForTreatment: false,
            consentFinancial: false,
            acknowledgementConsent: false,
            fullName: "",
            signatureDate: "",
          },
          patientInsuranceInfo: {
            insuranceId: "",
            memberNumber: "",
            relationshipToPatient: "",
          },
          schemes: [],
          lab: [],
          prescription: []
        },
      },
      submitted: false,
      showLoader: false,
      errors: [],
    };
  },
  computed: {
    currentTabComponent() {
      switch (this.currentTab) {
        case "patient-queue":
          return "PatientsListTab";
        case "appointment-queue":
          return "ReceptionAppointmentQueueTab";
        default:
          return "PatientsListTab";
      }
    },
    activeTabClass() {
      return "inline-block p-4 text-blue-600 bg-gray-100 rounded-t-lg active";
    },
    inactiveTabClass() {
      return "inline-block p-4 rounded-t-lg hover:text-gray-600 hover:bg-gray-50";
    },
    isLastTab() {
      return this.tab === "tab5";
    },
    activeUserType() {
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const parsedUser = JSON.parse(user);
          return parsedUser.UserType || null;
        }
        return null;
      } catch (error) {
        console.error('Error reading user type from local storage:', error);
        return null;
      }
    }
  },
  created() {},
  methods: {
    formatLabel(key) {
      return key
        .split(/(?=[A-Z])/)
        .join(" ")
        .replace(/^\w/, (c) => c.toUpperCase());
    },
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },
    applyFilter(option) {
      this.selectedFilter = option;
      this.isDropdownVisible = false;
    },
    handleNewPatientRegistrationModal() {
      this.showPatientRegistrationModal = true;
    },
    handleNewAppointmentModal() {
      this.showNewAppointmentModal = true;
    },
    closePatientRegistrationModal() {
      this.showPatientRegistrationModal = false;
    },
    closeAppointmentModal() {
      this.showNewAppointmentModal = false;
    },
    async handleSubmit(event) {
      console.log("PatientsPage handleSubmit called with event:", event);
      try {
        this.showLoader = true;
        const { response } = event;
        if (response.success) {
          this.$toast.success(response.message|| "Patient registered successfully");
          this.closePatientRegistrationModal();
          this.$refs.currentTabComponent.fetchPatientData();
        } else {
          this.$toast.error(response.message || "Failed to register patient");
        }
      } catch (error) {
        this.$toast.error("Failed to register patient");
        console.log(error);
      } finally {
        this.showLoader = false;
      }
    },
    async triggerSearch() {
      const params = {
        [this.selectedFilter]: this.searchQuery,
      };
      console.log("search params:", params);
      this.$refs.currentTabComponent.searchPatientData(params);
    },
    scrollToTop() {
      const modal = this.$refs.pstientInfoModal;
      if (modal) {
        console.log("patientRegModal");
        modal.scrollTop = 0;
      }
    },
    handleRequests(event){
      console.log('handleRequests', event);
    }
  },
  mounted() {
    initFlowbite();
  },
};
</script>

<style lang="css" scoped></style>